import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import OrderModal from '../Orders/OrderModal'; // Импортируем компонент OrderModal

const AccountModal = ({ account, onClose, onUpdate, onOpenOrder, user }) => {
    const [activeTab, setActiveTab] = useState('info');
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [orderRemainders, setOrderRemainders] = useState({}); // { [orderId]: { toll, fee, penalties } }
    
    // Новые состояния для модального окна приказа
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [modalMode, setModalMode] = useState('view'); // 'view' или 'edit'

    useEffect(() => {
        if (account) {
            setFormData({
                number: account.number.toString(),
                area: account.area != null && account.area !== '' ? Number(account.area).toFixed(2) : '',
                address: account.address || {
                    city: '',
                    street: '',
                    home: '',
                    apartment: ''
                },
                persons: account.persons && account.persons.length > 0
                    ? account.persons
                    : [{ name: '', inn: '' }]
            });
        }
    }, [account]);

    useEffect(() => {
        if (activeTab === 'orders' && account) {
            loadOrders();
        }
    }, [activeTab, account]);

    const loadOrders = async () => {
        if (!account) return;

        setOrdersLoading(true);
        try {
            const response = await api.getOrdersByAccount(account.number);
            const loadedOrders = response.data.data || [];
            setOrders(loadedOrders);

            // Загружаем сводку по выпискам для каждого приказа параллельно
            const remaindersMap = {};
            await Promise.all(
                loadedOrders.map(async (order) => {
                    try {
                        const summaryResp = await api.getStatementsSummaryByOrder(order.id);
                        // Бек возвращает {"message":"OK","data":{"totalCredit": <сумма>}}
                        const paidTotal = Number(summaryResp.data?.totalCredit ?? 0);

                        // Последовательно вычитаем из amountRecoveredToll, затем amountRecoveredFee, затем amountRecoveredPenalties
                        const toll = Number(order.amountRecoveredToll ?? 0);
                        const fee = Number(order.amountRecoveredFee ?? 0);
                        const penalties = Number(order.amountRecoveredPenalties ?? 0);

                        let remaining = paidTotal;

                        const remainToll = Math.max(0, toll - remaining);
                        remaining = Math.max(0, remaining - toll);

                        const remainFee = Math.max(0, fee - remaining);
                        remaining = Math.max(0, remaining - fee);

                        const remainPenalties = Math.max(0, penalties - remaining);
                        
                        const remainSumm = remainToll + remainFee + remainPenalties;

                        remaindersMap[order.id] = {
                            toll: remainToll,
                            fee: remainFee,
                            penalties: remainPenalties,
                            summary: remainSumm
                        };
                    } catch (err) {
                        console.error(`Ошибка загрузки сводки по выпискам для приказа ${order.id}:`, err);
                        remaindersMap[order.id] = { toll: null, fee: null, penalties: null };
                    }
                })
            );
            setOrderRemainders(remaindersMap);
        } catch (error) {
            console.error('Error loading orders:', error);
            setMessage('Ошибка загрузки приказов');
        } finally {
            setOrdersLoading(false);
        }
    };

    const formatDateToRussian = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    // Функции форматирования
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(value));
    };

    // Обработчик двойного клика по строке таблицы
    const handleRowDoubleClick = async (order) => {
        try {
            setLoading(true);
            const orderData = await api.getOrderById(order.id);
            setSelectedOrder(orderData.data);
            setModalMode('view');
            setShowOrderModal(true);
        } catch (error) {
            setMessage('Ошибка загрузки данных приказа');
            console.error('Error loading order details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Функция для закрытия модального окна приказа
    const handleOrderModalClose = () => {
        setShowOrderModal(false);
        setSelectedOrder(null);
    };

    // Функция для обновления данных после изменений в приказе
    const handleOrderUpdate = () => {
        if (activeTab === 'orders') {
            loadOrders();
        }
        if (onUpdate) {
            onUpdate();
        }
    };

    // Функция для возврата к модальному окну лицевого счета
    const handleBackToAccount = () => {
        setShowOrderModal(false);
        setSelectedOrder(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData) return;

        setLoading(true);
        try {
            const accountData = {
                number: parseInt(formData.number),
                area: formData.area ? parseFloat(formData.area) : null,
                address: {
                    city: formData.address.city,
                    street: formData.address.street,
                    home: formData.address.home,
                    apartment: parseInt(formData.address.apartment) || 0
                },
                persons: formData.persons.filter(p => p.name).map(p => ({ ...p, inn: p.inn || '' }))
            };

            await api.updateAccount(account.number, accountData);
            setMessage('Лицевой счет обновлен успешно');
            setEditMode(false);
            onUpdate();
        } catch (error) {
            setMessage('Ошибка обновления лицевого счета');
            console.error('Update account error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Вы уверены, что хотите удалить этот лицевой счет?')) {
            return;
        }

        try {
            await api.deleteAccount(account.number);
            onClose();
            onUpdate();
        } catch (error) {
            setMessage('Ошибка удаления лицевого счета');
            console.error('Delete account error:', error);
        }
    };

    const addPerson = () => {
        setFormData({
            ...formData,
            persons: [...formData.persons, { name: '', inn: '' }]
        });
    };

    const removePerson = (index) => {
        const newPersons = formData.persons.filter((_, i) => i !== index);
        setFormData({ ...formData, persons: newPersons });
    };

    const updatePerson = (index, field, value) => {
        const newPersons = [...formData.persons];
        newPersons[index][field] = value;
        setFormData({ ...formData, persons: newPersons });
    };

    const updateAddress = (field, value) => {
        setFormData({
            ...formData,
            address: {
                ...formData.address,
                [field]: value
            }
        });
    };

    if (!account) return null;

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>Лицевой счет № {account.number}</h3>
                        <button className="modal-close" onClick={onClose}>×</button>
                    </div>

                    <div className="modal-tabs">
                        <div className="tabs-header">
                            <button
                                className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                                onClick={() => setActiveTab('info')}
                            >
                                Инфо
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
                                onClick={() => setActiveTab('orders')}
                            >
                                Приказы
                            </button>
                        </div>

                        <div className="tab-content">
                            {activeTab === 'info' && (
                                <div className="info-tab">
                                    {message && (
                                        <div className={`message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                                            {message}
                                        </div>
                                    )}

                                    <div className="tab-actions">
                                        {!editMode ? (
                                            <>
                                                {user && (user.role === 'admin' || user.role === 'lawyer') && (
                                                    <>
                                                        <button
                                                            onClick={() => setEditMode(true)}
                                                            className="btn-primary"
                                                        >
                                                            Редактировать
                                                        </button>
                                                        <button
                                                            onClick={handleDelete}
                                                            className="btn-delete-modal"
                                                        >
                                                            Удалить
                                                        </button>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <div className="edit-actions">
                                                <button
                                                    onClick={handleSubmit}
                                                    disabled={loading}
                                                    className="btn-primary"
                                                >
                                                    {loading ? 'Сохранение...' : 'Сохранить'}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditMode(false);
                                                        setFormData({
                                                            number: account.number.toString(),
                                                            area: account.area != null && account.area !== '' ? Number(account.area).toFixed(2) : '',
                                                            address: account.address || {
                                                                city: '',
                                                                street: '',
                                                                home: '',
                                                                apartment: ''
                                                            },
                                                            persons: account.persons && account.persons.length > 0
                                                                ? account.persons
                                                                : [{ name: '', inn: '' }]
                                                        });
                                                    }}
                                                    className="btn-secondary"
                                                >
                                                    Отмена
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {editMode ? (
                                        <form onSubmit={handleSubmit} className="account-form">
                                        <div className="form-section">
                                            <h4>Основная информация</h4>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Номер счета *</label>
                                                    <input
                                                        type="number"
                                                        value={formData.number}
                                                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                                                        required
                                                        disabled
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Площадь</label>
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        value={formData.area}
                                                        onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <h4>Адрес</h4>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Город *</label>
                                                    <input
                                                        type="text"
                                                        value={formData.address.city}
                                                        onChange={(e) => updateAddress('city', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Улица *</label>
                                                    <input
                                                        type="text"
                                                        value={formData.address.street}
                                                        onChange={(e) => updateAddress('street', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Дом *</label>
                                                    <input
                                                        type="text"
                                                        value={formData.address.home}
                                                        onChange={(e) => updateAddress('home', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Квартира *</label>
                                                    <input
                                                        type="number"
                                                        value={formData.address.apartment}
                                                        onChange={(e) => updateAddress('apartment', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section">
                                            <div className="section-header">
                                                <h4>Собственники</h4>
                                                <button type="button" onClick={addPerson} className="btn-add">
                                                    + Добавить собственника
                                                </button>
                                            </div>

                                            {formData.persons.map((person, index) => (
                                                <div key={index} className="person-row">
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label>ФИО *</label>
                                                            <input
                                                                type="text"
                                                                value={person.name}
                                                                onChange={(e) => updatePerson(index, 'name', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>ИНН</label>
                                                            <input
                                                                type="text"
                                                                value={person.inn}
                                                                onChange={(e) => updatePerson(index, 'inn', e.target.value)}
                                                            />
                                                        </div>
                                                        {formData.persons.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removePerson(index)}
                                                                className="btn-remove"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        </form>
                                    ) : (
                                        <div className="account-details">
                                        <div className="details-section">
                                            <h4>Основная информация</h4>
                                            <div className="details-grid">
                                                <div className="detail-item">
                                                    <label>Номер счета:</label>
                                                    <span>{account.number}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Площадь:</label>
                                                    <span>{account.area != null && account.area !== '' ? Number(account.area).toFixed(2) : 'Не указана'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="details-section">
                                            <h4>Адрес</h4>
                                            <div className="details-grid">
                                                <div className="detail-item">
                                                    <label>Город:</label>
                                                    <span>{account.address?.city || 'Не указан'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Улица:</label>
                                                    <span>{account.address?.street || 'Не указана'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Дом:</label>
                                                    <span>{account.address?.home || 'Не указан'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Квартира:</label>
                                                    <span>{account.address?.apartment || 'Не указана'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="details-section">
                                            <h4>Собственники</h4>
                                            <div className="owners-list-detailed">
                                                {account.persons && account.persons.length > 0 ? (
                                                    account.persons.map((person, index) => (
                                                        <div key={index} className="owner-detail">
                                                            <div className="owner-info">
                                                                <strong>ФИО:</strong> {person.name}
                                                            </div>
                                                            <div className="owner-info">
                                                                <strong>ИНН:</strong> {person.inn}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p>Собственники не указаны</p>
                                                )}
                                            </div>
                                        </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'orders' && (
                                <div className="orders-tab">
                                    <h4>Приказы по лицевому счету</h4>

                                    {ordersLoading ? (
                                        <div className="loading">Загрузка приказов...</div>
                                    ) : orders.length === 0 ? (
                                        <div className="no-data">Нет приказов для этого лицевого счета</div>
                                    ) : (
                                        <div className="table-wrapper">
                                            <table className="orders-table">
                                                <thead>
                                                    <tr style={{ height: '50px', minHeight: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            № приказа
                                                        </th>
                                                        <th rowSpan="2" style={{ width: '80px', minWidth: '80px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Статус
                                                        </th>
                                                        <th rowSpan="2" style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <div>Дата принятия</div>
                                                            <div>заявления судом</div>
                                                        </th>
                                                        <th rowSpan="2" style={{ width: '200px', minWidth: '200px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            ФИО
                                                        </th>
                                                        <th colSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Период взыскания
                                                        </th>
                                                        <th colSpan="4" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Взысканная судом сумма
                                                        </th>
                                                        <th colSpan="4" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Остаток
                                                        </th>
                                                    </tr>
                                                    <tr style={{ height: '50px', minHeight: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            С
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            По
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Взносы
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Пени
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Пошлина
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Итого
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Взносы
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Пени
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Пошлина
                                                        </th>
                                                        <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                            Итого
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orders.map(order => (
                                                        <tr 
                                                            key={order.id}
                                                            onDoubleClick={() => handleRowDoubleClick(order)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <td>{order.caseNumber}</td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {order.isactive
                                                                    ? <span style={{ color: 'green', fontWeight: 'bold' }}>Активен</span>
                                                                    : <span style={{ color: 'red', fontWeight: 'bold' }}>Отменён</span>
                                                                }
                                                            </td>
                                                            <td>{formatDateToRussian(order.dateAcceptance)}</td>
                                                            <td>{order.person.name}</td>
                                                            <td>{formatDateToRussian(order.collectionPeriodFrom)}</td>
                                                            <td>{formatDateToRussian(order.collectionPeriodTo)}</td>
                                                            <td className="number-cell">{formatCurrency(order.amountRecoveredFee)}</td>
                                                            <td className="number-cell">{formatCurrency(order.amountRecoveredPenalties)}</td>
                                                            <td className="number-cell">{formatCurrency(order.amountRecoveredToll)}</td>
                                                            <td className="number-cell">{formatCurrency(order.amountRecoveredFee + order.amountRecoveredPenalties + order.amountRecoveredToll)}</td>
                                                            <td className="number-cell">
                                                                {orderRemainders[order.id]
                                                                    ? formatCurrency(orderRemainders[order.id].fee)
                                                                    : '...'}
                                                            </td>
                                                            <td className="number-cell">
                                                                {orderRemainders[order.id]
                                                                    ? formatCurrency(orderRemainders[order.id].penalties)
                                                                    : '...'}
                                                            </td>
                                                            <td className="number-cell">
                                                                {orderRemainders[order.id]
                                                                    ? formatCurrency(orderRemainders[order.id].toll)
                                                                    : '...'}
                                                            </td>
                                                            <td className="number-cell">
                                                                {orderRemainders[order.id]
                                                                    ? formatCurrency(orderRemainders[order.id].summary)
                                                                    : '...'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Модальное окно приказа */}
            {showOrderModal && selectedOrder && (
                <OrderModal
                    order={selectedOrder}
                    onClose={handleOrderModalClose}
                    onUpdate={handleOrderUpdate}
                    isEditMode={modalMode === 'edit'}
                    accountNumber={account.number}
                    onBackToAccount={handleBackToAccount}
                    user={user}
                />
            )}
        </>
    );
};

export default AccountModal;